import { useState, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Survey as SurveyModel } from '../types';

export function useSurvey(survey: SurveyModel) {
  // --- Estructura de encuesta ---
  const sortedSections = useMemo(
    () => survey.sections.slice().sort((a, b) => a.order - b.order),
    [survey.sections],
  );

  const flatQuestions = useMemo(
    () =>
      sortedSections.flatMap((sec) =>
        sec.questions
          .slice()
          .sort((q1, q2) => (q1.order || 0) - (q2.order || 0))
          .map((q) => ({ section: sec, question: q })),
      ),
    [sortedSections],
  );

  const totalQ = flatQuestions.length;
  const totalSteps = totalQ + 1;
  const totalSections = sortedSections.length;

  // --- Estado de navegación ---
  const [currentIdx, setCurrentIdx] = useState(0);
  const isFirst = currentIdx === 0;
  const isSubmitStep = currentIdx === totalQ;
  const isLast = currentIdx === totalSteps - 1;

  const goPrev = () => setCurrentIdx((i) => Math.max(0, i - 1));

  const goNext = async () => {
    if (isSubmitStep) {
      return;
    }

    // Validamos el campo actual
    const isValid = await methods.trigger(`responses.${currentQuestion?.id}`);

    if (isValid) {
      setCurrentIdx((i) => Math.min(totalSteps - 1, i + 1));
    }
  };

  // --- Datos actuales ---
  const currentSection = isSubmitStep
    ? sortedSections[sortedSections.length - 1]
    : flatQuestions[currentIdx].section;

  const currentQuestion = isSubmitStep
    ? null
    : flatQuestions[currentIdx].question;

  const currentSectionIndex = useMemo(
    () =>
      sortedSections.findIndex(
        (s) =>
          s.id ===
          (isSubmitStep
            ? flatQuestions[totalQ - 1].section.id
            : currentSection.id),
      ),
    [isSubmitStep, totalQ, flatQuestions, currentSection.id, sortedSections],
  );

  const currentQuestionIndex = useMemo(() => {
    if (isSubmitStep) {
      return null;
    }
    const qs = currentSection.questions
      .slice()
      .sort((q1, q2) => (q1.order || 0) - (q2.order || 0));
    return qs.findIndex((q) => q.id === currentQuestion!.id);
  }, [isSubmitStep, currentSection.questions, currentQuestion]);

  // --- Formularios y respuestas ---
  const methods = useForm({ mode: 'onChange' });
  const { handleSubmit, control } = methods;
  const allValues = useWatch({ control });

  // IDs de las preguntas
  const questionIds = useMemo(() => flatQuestions.map((f) => f.question.id), [
    flatQuestions,
  ]);

  // Conteo de respondidas
  const answeredCount = questionIds.filter((id) => {
    const val = allValues?.questions?.[id];
    return val !== undefined && val !== '';
  }).length;
  const allAnswered = answeredCount === totalQ;

  return {
    // Formulario
    methods,
    handleSubmit,
    control,

    // Navegación
    currentIdx,
    goPrev,
    goNext,
    isFirst,
    isLast,
    isSubmitStep,

    // Cuenta de pasos
    totalQ,
    totalSteps,
    totalSections,

    // Datos actuales
    currentSection,
    currentQuestion,
    currentSectionIndex,
    currentQuestionIndex,

    // Respuestas
    allAnswered,
  };
}
